import { DateTime } from "luxon";
import { BookingScheduleInterface } from "../../../../features/Booking-Form/typings/booking.types";
import { TeamMember } from "../../../../features/Booking-Form/typings/team-member";

type SortScheduleInput = {
  schedules: BookingScheduleInterface[];
  users?: TeamMember[];
};

type NameParts = {
  firstName: string;
  surname: string;
};

function buildUserNameMap(users: TeamMember[] | undefined): Map<string, NameParts> {
  return new Map(
    (users ?? []).map(user => [
      user.userId,
      { firstName: user.firstName?.trim() ?? "", surname: user.surname?.trim() ?? "" }
    ])
  );
}

function toDayKey(iso: string): string {
  const date = DateTime.fromISO(iso);
  return date.isValid ? date.toISODate() ?? "" : "";
}

function byName(a: NameParts, b: NameParts): number {
  const surnameCompare = a.surname.localeCompare(b.surname, undefined, { sensitivity: "base" });
  if (surnameCompare !== 0) {
    return surnameCompare;
  }
  return a.firstName.localeCompare(b.firstName, undefined, { sensitivity: "base" });
}

function parseBookedForName(userBookedFor: string | undefined): NameParts {
  if (!userBookedFor) {
    return { firstName: "", surname: "" };
  }

  const parts = userBookedFor.trim().split(/\s+/).filter(Boolean);
  if (parts.length <= 1) {
    return { firstName: userBookedFor.trim(), surname: "" };
  }

  const surname = parts.at(-1) ?? "";
  const firstName = parts.slice(0, -1).join(" ");
  return { firstName, surname };
}

function parseFullName(rawName: string | undefined): NameParts | undefined {
  if (!rawName?.trim()) {
    return undefined;
  }
  return parseBookedForName(rawName);
}

function readStringField(source: Record<string, unknown>, key: string): string | undefined {
  const value = source[key];
  return typeof value === "string" ? value : undefined;
}

function getExternalInformationName(schedule: BookingScheduleInterface): NameParts | undefined {
  const externalInfo = (schedule as BookingScheduleInterface & { externalInformation?: unknown })
    .externalInformation;
  if (!externalInfo || typeof externalInfo !== "object") {
    return undefined;
  }

  const info = externalInfo as Record<string, unknown>;
  const fullName =
    readStringField(info, "name") ??
    readStringField(info, "fullName") ??
    readStringField(info, "displayName");
  const parsedFullName = parseFullName(fullName);
  if (parsedFullName) {
    return parsedFullName;
  }

  const firstName =
    readStringField(info, "firstName") ??
    readStringField(info, "firstname") ??
    readStringField(info, "givenName");
  const surname =
    readStringField(info, "surname") ??
    readStringField(info, "lastName") ??
    readStringField(info, "lastname") ??
    readStringField(info, "familyName");

  if (firstName?.trim() || surname?.trim()) {
    return { firstName: firstName?.trim() ?? "", surname: surname?.trim() ?? "" };
  }

  return undefined;
}

function resolveName(
  usersById: Map<string, NameParts>,
  schedule: BookingScheduleInterface
): NameParts {
  const externalInformationName = getExternalInformationName(schedule);
  if (externalInformationName) {
    return externalInformationName;
  }

  const externalName = parseFullName(schedule.externalName);
  if (externalName) {
    return externalName;
  }

  return usersById.get(schedule.userBookedFor ?? "") ?? parseBookedForName(schedule.userBookedFor);
}

export const sortSchedulesByLastNameTestables = {
  buildUserNameMap,
  toDayKey,
  byName,
  parseBookedForName,
  parseFullName,
  readStringField,
  getExternalInformationName,
  resolveName
};

export function sortSchedulesByLastName({ schedules, users }: Readonly<SortScheduleInput>) {
  const usersById = buildUserNameMap(users);

  return [...schedules].sort((a, b) => {
    const dayCompare = toDayKey(a.startDate).localeCompare(toDayKey(b.startDate));
    if (dayCompare !== 0) {
      return dayCompare;
    }

    const aName = resolveName(usersById, a);
    const bName = resolveName(usersById, b);
    const nameCompare = byName(aName, bName);
    if (nameCompare !== 0) {
      return nameCompare;
    }

    const startCompare =
      DateTime.fromISO(a.startDate).valueOf() - DateTime.fromISO(b.startDate).valueOf();
    if (startCompare !== 0) {
      return startCompare;
    }

    return a.id - b.id;
  });
}
